import React, { useEffect, useRef, useMemo } from 'react';
import { useBookingContext } from '../BookingContext';
import { type TimeSlotAvailability } from '../../../models/booking/TimeSlotAvailability';
import Tooltip from '../../Tooltip';

interface SimulatorCardProps {
  name: string;
  total: number;
  count: number;
  disabled: boolean;
  onDecrement: () => void;
  onIncrement: () => void;
  tooltip?: string;
}

const SimulatorCard: React.FC<SimulatorCardProps> = ({ name, total, count, disabled, onDecrement, onIncrement, tooltip }) => (
  <div className={`card${total === 0 ? ' opacity-50' : ''}`}>
    <div className="card-body py-2 px-3 d-flex align-items-center justify-content-between">
      <div className="d-flex align-items-center gap-2">
        <span className="text-white fw-semibold" style={{ whiteSpace: 'nowrap' }}>{name}</span>
        {tooltip && <Tooltip id={`${name}-tooltip`} title={tooltip} />}
        <span className="badge bg-primary">up to {total}</span>
      </div>
      <div className="d-flex align-items-center gap-2">
        <button
          type="button"
          className="btn btn-outline-secondary booking-wizard-nav-button"
          onClick={onDecrement}
          disabled={disabled || count <= 0}
        >
          -
        </button>
        <span className="text-white" style={{ minWidth: 28, textAlign: 'center' }}>{count}</span>
        <button
          type="button"
          className="btn btn-outline-secondary booking-wizard-nav-button"
          onClick={onIncrement}
          disabled={disabled || count >= total}
        >
          +
        </button>
      </div>
    </div>
  </div>
);

interface SimulatorSectionProps {
  slots: TimeSlotAvailability[];
  isLoadingSlots: boolean;
}

const SimulatorSection: React.FC<SimulatorSectionProps> = ({ slots, isLoadingSlots }) => {
  const { booking, updateBooking } = useBookingContext();
  const simulatorsRef = useRef({ fullMotion: booking.fullMotionSimulators, esports: booking.esportsSimulators });
  simulatorsRef.current = { fullMotion: booking.fullMotionSimulators, esports: booking.esportsSimulators };

  const availableCounts = useMemo(() => ({
    fullMotionTotal: Math.max(0, ...slots.map(s => s.fullMotion?.available ?? 0)),
    esportsTotal: Math.max(0, ...slots.map(s => s.esports?.available ?? 0)),
  }), [slots]);

  useEffect(() => {
    if (isLoadingSlots || slots.length === 0) return;

    const { fullMotionTotal, esportsTotal } = availableCounts;
    const { fullMotion: currentFM, esports: currentES } = simulatorsRef.current;
    const nextFM = Math.min(Math.max(currentFM, 0), fullMotionTotal);
    const nextES = Math.min(Math.max(currentES, 0), esportsTotal);

    if (nextFM !== currentFM || nextES !== currentES) {
      updateBooking({ fullMotionSimulators: nextFM, esportsSimulators: nextES });
    }
  }, [availableCounts, updateBooking, isLoadingSlots, slots.length]);

  const changeFullMotion = (delta: number) => {
    const next = Math.min(Math.max(booking.fullMotionSimulators + delta, 0), availableCounts.fullMotionTotal);
    updateBooking({ fullMotionSimulators: next });
  };

  const changeEsports = (delta: number) => {
    const next = Math.min(Math.max(booking.esportsSimulators + delta, 0), availableCounts.esportsTotal);
    updateBooking({ esportsSimulators: next });
  };

  return (
    <div className="d-flex flex-column gap-1">
      <label className="settings-field-label">Simulators:</label>
      <div className="d-flex flex-column gap-2">
        <SimulatorCard
          name="Full Motion"
          total={availableCounts.fullMotionTotal}
          count={booking.fullMotionSimulators}
          disabled={isLoadingSlots}
          onDecrement={() => changeFullMotion(-1)}
          onIncrement={() => changeFullMotion(1)}
          tooltip="Can be used by people starting from 14 years old."
        />
        <SimulatorCard
          name="Esports"
          total={availableCounts.esportsTotal}
          count={booking.esportsSimulators}
          disabled={isLoadingSlots}
          onDecrement={() => changeEsports(-1)}
          onIncrement={() => changeEsports(1)}
        />
      </div>

    </div>
  );
};

export default SimulatorSection;
